import { Box, CircularProgress, Alert, Checkbox } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { practiceUsersApi } from "apis";
import CommonPopup, { PopupButton } from "components/common/CommonPopup";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import { useToast } from "contexts/ToastContext";
import { BranchDto } from "libs/cellera-sdk";
import { isNil } from "lodash";
import { useEffect, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

export interface SelectBranchModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSelect: (branch?: BranchDto) => void;
  readonly isLoading?: boolean;
}

export const SelectBranchModal = ({
  open,
  onClose,
  onSelect,
  isLoading = false,
}: SelectBranchModalProps) => {
  const { showError } = useToast();
  const { colors } = useCelleraTheme();
  const [selectedBranch, setSelectedBranch] = useState<BranchDto>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch branches using React Query
  const {
    data: branchesResponse,
    isLoading: branchesLoading,
    error,
  } = useQuery({
    queryKey: ["practice-branches"],
    queryFn: async () => {
      const branches = await practiceUsersApi.getPracticeBranches();
      return branches;
    },
    enabled: open,
    retry: 2,
  });
  useEffect(() => {
    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch data";
      showError(errorMessage);
    }
  }, [error, showError]);

  const branches: BranchDto[] = Array.isArray(branchesResponse)
    ? branchesResponse
    : branchesResponse
      ? [branchesResponse as BranchDto]
      : [];
  const isAnyLoading = isLoading || branchesLoading;

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Table columns
  const columns: CustomTableColumn<BranchDto>[] = [
    {
      id: "select",
      label: "",
      width: 50,
      getValue: (branch) => (
        <Checkbox
          checked={selectedBranch?.id === branch.id}
          onChange={(event) =>
            setSelectedBranch(event.target.checked ? branch : undefined)
          }
          sx={{
            color: colors.primary.main,
            "&.Mui-checked": {
              color: colors.primary.main,
            },
          }}
        />
      ),
    },
    {
      id: "address",
      label: "Address",
      getValue: (branch) => branch.address || "--",
      sortable: true,
      sortAccessor: (branch) => branch.address || "",
    },
    {
      id: "emailAddress",
      label: "Email Address",
      getValue: (branch) => branch.emailAddress || "--",
      sortable: true,
      sortAccessor: (branch) => branch.emailAddress || "",
    },
    {
      id: "primaryPhoneNumber",
      label: "Primary Phone Number",
      getValue: (branch) => branch.primaryPhoneNumber || "--",
      sortable: true,
      sortAccessor: (branch) => branch.primaryPhoneNumber || "",
    },
    {
      id: "faxNumber",
      label: "Fax Number",
      getValue: (branch) => branch.faxNumber || "--",
      sortable: true,
      sortAccessor: (branch) => branch.faxNumber || "",
    },
  ];

  const buttons: PopupButton[] = [
    {
      label: "Cancel",
      onClick: onClose,
      variant: "outlined",
      disabled: isAnyLoading,
      sx: {
        borderColor: colors.border.main,
        borderRadius: "80px",
        width: "150px",
        backgroundColor: colors.background.secondary,
        color: colors.text.primary,
        "&:hover": {
          borderColor: colors.border.main,
          backgroundColor: colors.background.highlight,
          color: colors.text.primary,
        },
      },
    },
    {
      label: "Save",
      onClick: () => onSelect(selectedBranch),
      variant: "contained",
      disabled: isAnyLoading || isNil(selectedBranch),
      loading: isAnyLoading,
      sx: {
        borderColor: colors.border.main,
        borderRadius: "80px",
        width: "150px",
        color: colors.text.inverse,
        backgroundColor: colors.primary.main,
        "&:hover": {
          borderColor: colors.border.main,
          backgroundColor: colors.background.highlight,
          color: colors.text.primary,
        },
      },
    },
  ];
  return (
    <CommonPopup
      open={open}
      onClose={onClose}
      actionsSx={{
        p: "16px",
      }}
      title="Select Branch"
      buttons={buttons}
      maxWidth="lg"
      minHeight={400}
      fullWidth
    >
      <Box sx={{ p: 2 }}>
        {branchesLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress size={40} />
          </Box>
        ) : branches.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No branches available.
          </Alert>
        ) : (
          <CustomTable
            data={branches}
            columns={columns}
            totalCount={branches.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={branchesLoading}
            minHeight={300}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Box>
    </CommonPopup>
  );
};
